
import { v4 as uuidv4 } from "uuid";
import { AbilityScoreExpandedType, AreaType, AttackType, CastingTime, CharacterLimitationType, DamageType, DataType, EffectType, ItemType, SpellLevel, TargetType } from "@dndModels/Types";
import { 
  ATTACK_TYPES, 
  SIZE_MAP 
} from "@dndModels/Constants";
import { UpgradableNumber } from "@dndModels/misc/UpgradableNumber";
import CommonUtilities from "@utilities/common_utilities";
// import { 
//     ModelBase, 
//     FeatureBase, 
//     BaseItem,
//     RollPlus
//   } from ".";

const common_util = CommonUtilities.getInstance();

export type LimitationType = "Character" | "Item" | "Weapon" | "Ability" | "Spell" | "Creature" | "None";

export class Limitations {
  level_type: "Character" | "Class" | "Inherit" = "Inherit";
  limitation_type: LimitationType = "None";
  for_class_id = "";
  limits: Limitation[] = [];
  
  constructor(obj?: any) {
    if (obj) {
      this.level_type = obj.level_type ? obj.level_type : "Inherit";
      this.limitation_type = obj.limitation_type ? obj.limitation_type : "None";
      this.for_class_id = obj.for_class_id ? obj.for_class_id : "";
      this.limits = obj.limits ? obj.limits.map((o: any) => {
        o.limitation_type = this.limitation_type;
        return LimitationConstructor(o);
      }) : [];
    }
  }

  toDBObj(): any {
    return {
      level_type: this.level_type,
      limitation_type: this.limitation_type,
      for_class_id: this.for_class_id,
      limits: this.limits.map(o => o.toDBObj())
    };
  }

  copy(copyMe: Limitations, drop_ids = false): void {
    this.level_type = copyMe.level_type;
    this.limitation_type = copyMe.limitation_type;
    this.for_class_id = copyMe.for_class_id;
    this.limits = copyMe.limits.map(o => o.clone(drop_ids));
  }

  clone(drop_ids = false): Limitations {
    const new_obj = new Limitations();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

export class Limitation {
  true_id = "";
  limit_level = 1; // If it uses different limitations for different levels, this is the min level it uses for this limit
  limitation_type: LimitationType = "None";
  nested: Limitation[] = [];

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.limitation_type = obj.limitation_type ? obj.limitation_type : "None";
      this.limit_level = obj.limit_level ? obj.limit_level : 1;
      this.nested = obj.nested ? obj.nested.map((o: any) => {
        // console.log(o);
        return LimitationConstructor(o);
      }) : [];
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      limitation_type: this.limitation_type,
      limit_level: this.limit_level,
      nested: this.nested.map(o => o.toDBObj())
    };
  }

  copy(copyMe: Limitation, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.limitation_type = copyMe.limitation_type;
    this.limit_level = copyMe.limit_level;
    this.nested = copyMe.nested.map(o => o.clone());
  }

  clone(drop_ids = false): Limitation {
    const new_obj = new Limitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

// export class LimitationsPotence {
//   true_id: string = "";
//   level: number = 1;
//   limitations: Limitations = new Limitations();

//   constructor(obj?: any, log_it: boolean = false) {
//     this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
//     if (obj) {
//       this.level = obj.level ? obj.level : 1;
//       this.limitations = obj.limitations ? new Limitations(obj.limitations) : new Limitations();
//     }
//   }

//   toDBObj(): any {
//     return {
//       true_id: this.true_id,
//       level: this.level,
//       limitations: this.limitations.toDBObj(),
//     };
//   }

//   clone(drop_ids: boolean = false): LimitationsPotence {
//     const new_obj = new LimitationsPotence();
//     new_obj.copy(this, drop_ids);
//     return new_obj;
//   }

//   copy(copyMe: LimitationsPotence, drop_ids: boolean = false): void {
//     if (!drop_ids) {
//       this.true_id = copyMe.true_id;
//     }
//     this.level = copyMe.level;
//     this.limitations = copyMe.limitations.clone();
//   }
// }
  
/**
 * These are limitations about a character.  
 * They're used on feats, items, etc that can only be used by characters 
 * that fit their requirements.
 */
export class CharacterLimitation extends Limitation {
  type: CharacterLimitationType = ""; // race, subrace, game_class, subclass, gift, alignment, min_ability_score, spellcaster, proficiency
  variant_specific = false;
  ids: string[] = [];
  from_source_types: string[] = []; // This is used when type === "tags".  It's to specify the source types to check for the tags.  If empty then it does all source types.
  values: number[] = [];
  exclusive = false; // If true then the character has to NOT meet the Limitation

  constructor(obj?: any) {
    super(obj);
    this.limitation_type = "Character";
    if (obj) {
      this.type = obj.type ? obj.type : "";
      this.variant_specific = obj.variant_specific ? obj.variant_specific : false;
      switch (this.type as string) {
        case "Race": this.type = "race"; break;
        case "Subrace": this.type = "subrace"; break;
        case "Subace": this.type = "subrace"; break;
        case "GameClass": this.type ="game_class"; break;
        case "Subclass": this.type = "subclass"; break;
        case "Gift": this.type = "gift"; break;
      }
      this.ids = obj.ids ? obj.ids : [];
      this.from_source_types = obj.from_source_types ? obj.from_source_types : [];
      this.values = obj.values ? [...obj.values] : (obj.min_value ? [obj.min_value] : []);
      if (this.values.length === 0 && 
        ["min_ability_score","spell","level","game_class","subclass"].includes(this.type)) {
        switch (this.type) {
          case "min_ability_score": this.values = [13]; break;
          case "spell": this.values = [1, 0]; break;
          case "level": this.values = [1]; break;
          case "game_class": this.values = [1]; break;
          case "subclass": this.values = [1]; break;
        }
      }
      this.exclusive = obj.exclusive ? obj.exclusive : false;
    }
  }

  toDetailString() {
    let str = "";
    switch (this.type) {
      case "alignment": str = `Alignment: ${this.ids.join(", or ")}`; break;
      case "at_least_1_language": break;
      case "at_least_1_weapon": break;
      case "class_group": break;
      case "class_or_class_group": break;
      case "dragonmark": str = "Dragonmarked"; break;
      case "min_ability_score": str = `Min Ability Score: ${common_util.customJoin({ arr: this.ids, separator: ", ", lastSeparator: ", or", onlySeparator: " or "})} ${this.values[0]}`; break;
      case "proficiency": 
        // debugger;
        str = `Proficiency in {list_of_type:skill,tool,armor_type,weapon_keyword,item;${this.ids.join(",")}}`; 
      break;
      case "spellcaster": 
        if (this.ids.length > 0) {
          str = `Spellcasting Types: ${common_util.customJoin({ arr: this.ids, separator: ", ", lastSeparator: ", or", onlySeparator: " or "})}`; 
        } else {
          str = "Spellcaster";
        }
      break;
      case "spells_of_schools": str = `At least ${this.values[0]} spells of at least Level ${this.values[1]} of ${common_util.customJoin({ arr: this.ids, separator: ", ", lastSeparator: ", or", onlySeparator: " or "})}`; break;
      case "tags": str = this.ids.join(", "); break;
      case "feature_of_type": str = `Feature of Type: ${common_util.customJoin({ arr: this.ids, separator: ", ", lastSeparator: ", or", onlySeparator: " or "})}`; break;
      case "level": str = `Level: ${this.values[0]}+`; break;
      case "game_class": 
        str = `{list_of_type:${this.type};${this.ids.join(",")}}`;
        if (this.values[0] > 1) {
          str += ` ${this.values[0]}+`; 
        }
      break;
      default: str = `{list_of_type:${this.type};${this.ids.join(",")}}`; break;
    } 
    if (this.variant_specific) {
      str += " (Variant Specific)";
    }
    if (this.exclusive) {
      str = "Not " + str;
    }
    return str;
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      type: this.type,
      variant_specific: this.variant_specific,
      ids: [...this.ids],
      from_source_types: this.from_source_types,
      values: [...this.values],
      exclusive: this.exclusive
    };
  }

  copy(copyMe: CharacterLimitation, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.type = copyMe.type;
    this.variant_specific = copyMe.variant_specific;
    this.ids = [...copyMe.ids];
    this.from_source_types = [...copyMe.from_source_types];
    this.values = [...copyMe.values];
    this.exclusive = copyMe.exclusive;
  }

  clone(drop_ids = false): CharacterLimitation {
    const new_obj = new CharacterLimitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}
 
/**
 * These are limitations about items.  
 * They're used in some features that affect the use of items.
 * I'm specifically doing it for Artificer Infusion - Replicate Magic Item.
 */
export class ItemLimitation extends Limitation {
  item_types: ItemType[] = [];
  exclude_item_types = false;
  weapon_keyword_ids: string[] = [];
  // exclude_keyword_ids: boolean = false;
  excluded_keyword_ids: string[] = [];
  mik_ids: string[] = [];
  // exclude_mik_ids: boolean = false;
  excluded_mik_ids: string[] = [];
  // item_ids: string[] = []; // This field is || with the rest, while they're all &&
  pseudo_item_ids: string[] = [];

  get item_ids() {
    const item_ids = this.pseudo_item_ids.filter(o => !o.includes(",") && o.includes("B:")).map(o => {
      return o.replace("B:","");
    });
    return item_ids;
  }

  set item_ids(item_ids: string[]) {
    this.pseudo_item_ids = item_ids.map(o => `B:${o}`);
  }

  min_rarity = 0;
  max_rarity = 5;
  magical: boolean | null = null; // null = don't filter on this
  attack_types: AttackType[] = [];
  damage_types: (DamageType | "Selectable")[] = [];
  damage_selectable_key = "";

  constructor(obj?: any) {
    super(obj);
    this.limitation_type = "Item";
    if (obj) {
      this.item_types = obj.item_types ? obj.item_types : [];
      this.exclude_item_types = obj.exclude_item_types ? obj.exclude_item_types : false;
      this.weapon_keyword_ids = obj.weapon_keyword_ids ? obj.weapon_keyword_ids : [];
      this.excluded_keyword_ids = obj.excluded_keyword_ids ? obj.excluded_keyword_ids : [];
      if (this.excluded_keyword_ids.length === 0 && 
        this.weapon_keyword_ids.length > 0 && 
        obj.exclude_keyword_ids) {
        this.excluded_keyword_ids = this.weapon_keyword_ids;
        this.weapon_keyword_ids = [];
      }
      this.mik_ids = obj.mik_ids ? obj.mik_ids : [];
      this.excluded_mik_ids = obj.excluded_mik_ids ? obj.excluded_mik_ids : [];
      if (this.excluded_mik_ids.length === 0 && 
        this.mik_ids.length > 0 && 
        obj.exclude_mik_ids) {
        this.excluded_mik_ids = this.mik_ids;
        this.mik_ids = [];
      }
      if (obj.pseudo_item_ids) {
        this.pseudo_item_ids = obj.pseudo_item_ids;
      } else {
        const item_ids: string[] = obj.item_ids ? obj.item_ids : (obj.weapon_ids ? obj.weapon_ids : []);
        this.pseudo_item_ids = item_ids.map(o => `B:${o}`);
      }

      this.min_rarity = obj.min_rarity ? obj.min_rarity : 0;
      this.max_rarity = obj.max_rarity !== undefined ? obj.max_rarity : 5;
      this.magical = obj.magical !== undefined ? obj.magical : null;
      this.attack_types = obj.attack_types ? obj.attack_types : [];
      this.attack_types = this.attack_types.filter(o => ATTACK_TYPES.includes(o));
      this.damage_types = obj.damage_types ? obj.damage_types : [];
      this.damage_selectable_key = obj.damage_selectable_key ? obj.damage_selectable_key : "";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      item_types: this.item_types,
      exclude_item_types: this.exclude_item_types,
      weapon_keyword_ids: this.weapon_keyword_ids,
      excluded_keyword_ids: this.excluded_keyword_ids,
      mik_ids: this.mik_ids,
      excluded_mik_ids: this.excluded_mik_ids,
      item_ids: [...this.item_ids],
      pseudo_item_ids: this.pseudo_item_ids,
      min_rarity: this.min_rarity,
      max_rarity: this.max_rarity,
      magical: this.magical,
      attack_types: [...this.attack_types],
      damage_types: [...this.damage_types],
      damage_selectable_key: this.damage_selectable_key,
    };
  }

  copy(copyMe: ItemLimitation, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.item_types = [...copyMe.item_types];
    this.exclude_item_types = copyMe.exclude_item_types;
    this.weapon_keyword_ids = [...copyMe.weapon_keyword_ids];
    this.excluded_keyword_ids = [...copyMe.excluded_keyword_ids];
    this.mik_ids = [...copyMe.mik_ids];
    this.excluded_mik_ids = [...copyMe.excluded_mik_ids];
    // this.item_ids = [...copyMe.item_ids];
    this.pseudo_item_ids = [...copyMe.pseudo_item_ids];

    this.min_rarity = copyMe.min_rarity;
    this.max_rarity = copyMe.max_rarity;
    this.magical = copyMe.magical;
    this.attack_types = [...copyMe.attack_types];
    this.damage_types = [...copyMe.damage_types];
    this.damage_selectable_key = copyMe.damage_selectable_key;
  }

  clone(drop_ids = false): ItemLimitation {
    const new_obj = new ItemLimitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  isEmpty(except: string[] = []): boolean {
    return (this.item_types.length === 0 || except.includes("item_types")) && 
      (this.weapon_keyword_ids.length === 0 || except.includes("weapon_keyword_ids")) && 
      (this.mik_ids.length === 0 || except.includes("mik_ids")) && 
      (this.item_ids.length === 0 || except.includes("item_ids")) && 
      (this.pseudo_item_ids.length === 0 || except.includes("pseudo_item_ids") || (except.includes("item_ids") && this.item_ids.length === this.pseudo_item_ids.length)) &&
      (this.min_rarity === 0 || except.includes("min_rarity")) && 
      (this.max_rarity === 5 || except.includes("max_rarity")) && 
      (this.magical === null || except.includes("magical")) && 
      (this.attack_types.length === 0 || except.includes("attack_types")) && 
      (this.damage_types.length === 0 || except.includes("damage_types"));
  }
}
/**
 * These are limitations about weapons.  
 * They're used in some features that affect the use of weapons.
 * They can also be used to apply to unarmed strikes 
 * or abilities in some situations.
 */
export class WeaponLimitation extends ItemLimitation {
  proficient: "Yes" | "No" | "Ignore" = "Ignore";
  have_weapon_mastery: "Yes" | "No" | "Ignore" = "Ignore";
  attack_mods: AbilityScoreExpandedType[] = [];

  constructor(obj?: any) {
    super(obj);
    this.limitation_type = "Weapon";
    if (obj) {
      this.proficient = obj.proficient ? obj.proficient : "Ignore";
      this.have_weapon_mastery = obj.have_weapon_mastery ? obj.have_weapon_mastery : "Ignore";
      this.attack_mods = obj.attack_mods ? obj.attack_mods : [];
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      proficient: this.proficient,
      have_weapon_mastery: this.have_weapon_mastery,
      attack_mods: this.attack_mods
    };
  }

  copy(copyMe: WeaponLimitation, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.proficient = copyMe.proficient;
    this.have_weapon_mastery = copyMe.have_weapon_mastery;
    this.attack_mods = [...copyMe.attack_mods];
  }

  clone(drop_ids = false): WeaponLimitation {
    const new_obj = new WeaponLimitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  isEmpty(except: string[] = []): boolean {
    return super.isEmpty(except) && 
      (this.mik_ids.length === 0 || except.includes("mik_ids")) && 
      (this.proficient === "Ignore" || except.includes("proficient")) && 
      (this.have_weapon_mastery === "Ignore" || except.includes("have_weapon_mastery")) &&
      (this.attack_mods.length === 0);
  }
}
/**
 * These are limitations about abilities. 
 */
export class AbilityLimitation extends Limitation {
  name = "";
  ability_tag = "";
  source_types: DataType[] = [];
  source_ids: string[] = []; // can be cfs.source_id, or can be "This Item".  If source_id is for a class, it includes ones from subclasses of it, as well as special_features of it, but not feats gained through it.
  attack_types: string[] = [];
  damage_types: string[] = [];
  effect_types: string[] = [];
  target_types: TargetType[] = [];
  area_types: AreaType[] = [];
  casting_times: CastingTime[] = [];
  attack_mods: AbilityScoreExpandedType[] = [];
  uses_resource: string[] = [];
  special_casting_time = "";
  ritual: "Yes" | "No" | "Ignore" = "Ignore";
  concentration: "Yes" | "No" | "Ignore" = "Ignore";
  min_duration: number | null = null;
  max_duration: number | null = null;
  min_range: number | null = null;
  max_range: number | null = null;
  touch: "Yes" | "No" | "Ignore" = "Ignore"; 
  self: "Yes" | "No" | "Ignore" = "Ignore";
  twinnable_1dd_ua7: "Yes" | "No" | "Ignore" = "Ignore";
  disease: "Yes" | "No" | "Ignore" = "Ignore";
  contaminated: "Yes" | "No" | "Ignore" = "Ignore";
  charm: "Yes" | "No" | "Ignore" = "Ignore";
  sight: "Yes" | "No" | "Ignore" = "Ignore";
  verbal: "Yes" | "No" | "Ignore" = "Ignore";
  somatic: "Yes" | "No" | "Ignore" = "Ignore";
  material: "Yes" | "No" | "Ignore" = "Ignore";

  constructor(obj?: any) {
    super(obj);
    this.limitation_type = "Ability";
    if (obj) {
      this.name = obj.name ? obj.name : [];
      this.ability_tag = obj.ability_tag ? obj.ability_tag : "";
      this.source_types = obj.source_types ? obj.source_types : [];
      this.source_ids = obj.source_ids ? obj.source_ids : [];
      this.attack_types = obj.attack_types ? obj.attack_types : [];
      this.damage_types = obj.damage_types ? obj.damage_types : [];
      this.target_types = obj.target_types ? obj.target_types : [];
      this.area_types = obj.area_types ? obj.area_types : [];
      this.effect_types = obj.effect_types ? obj.effect_types : [];
      this.casting_times = obj.casting_times ? obj.casting_times : [];
      this.attack_mods = obj.attack_mods ? obj.attack_mods : [];
      this.uses_resource = obj.uses_resource ? obj.uses_resource : [];
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
      this.ritual = obj.ritual ? obj.ritual : "Ignore";
      this.concentration = obj.concentration ? obj.concentration : "Ignore";
      this.min_duration = obj.min_duration ? obj.min_duration : null;
      this.max_duration = obj.max_duration ? obj.max_duration : null;
      this.min_range = obj.min_range ? obj.min_range : null;
      this.max_range = obj.max_range ? obj.max_range : null;
      this.touch = obj.touch ? obj.touch : "Ignore";
      this.self = obj.self ? obj.self : "Ignore";
      this.twinnable_1dd_ua7 = obj.twinnable_1dd_ua7 ? obj.twinnable_1dd_ua7 : "Ignore";
      this.disease = obj.disease ? obj.disease : "Ignore";
      this.contaminated = obj.contaminated ? obj.contaminated : "Ignore";
      this.charm = obj.charm ? obj.charm : "Ignore";
      this.sight = obj.sight ? obj.sight : "Ignore";
      this.verbal = obj.verbal ? obj.verbal : "Ignore";
      this.somatic = obj.somatic ? obj.somatic : "Ignore";
      this.material = obj.material ? obj.material : "Ignore";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      name: this.name,
      ability_tag: this.ability_tag,
      source_types: this.source_types,
      source_ids: [...this.source_ids],
      attack_types: [...this.attack_types],
      damage_types: [...this.damage_types],
      target_types: this.target_types,
      effect_types: [...this.effect_types],
      area_types: this.area_types,
      casting_times: [...this.casting_times],
      attack_mods: this.attack_mods,
      uses_resource: this.uses_resource,
      special_casting_time: this.special_casting_time,
      ritual: this.ritual,
      concentration: this.concentration,
      min_duration: this.min_duration,
      max_duration: this.max_duration,
      min_range: this.min_range,
      max_range: this.max_range,
      touch: this.touch,
      self: this.self,
      twinnable_1dd_ua7: this.twinnable_1dd_ua7,
      disease: this.disease,
      contaminated: this.contaminated,
      charm: this.charm,
      sight: this.sight,
      verbal: this.verbal,
      somatic: this.somatic,
      material: this.material,
    };
  }

  copy(copyMe: AbilityLimitation, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.source_types = [...copyMe.source_types];
    this.source_ids = [...copyMe.source_ids];
    this.name = copyMe.name;
    this.ability_tag = copyMe.ability_tag;
    this.attack_types = [...copyMe.attack_types];
    this.effect_types = [...copyMe.effect_types];
    this.damage_types = [...copyMe.damage_types];
    this.target_types = [...copyMe.target_types];
    this.area_types = [...copyMe.area_types];
    this.casting_times = [...copyMe.casting_times];
    this.attack_mods = [...copyMe.attack_mods];
    this.uses_resource = [...copyMe.uses_resource];
    this.special_casting_time = copyMe.special_casting_time;
    this.ritual = copyMe.ritual;
    this.concentration = copyMe.concentration;
    this.min_duration = copyMe.min_duration;
    this.max_duration = copyMe.max_duration;
    this.min_range = copyMe.min_range;
    this.max_range = copyMe.max_range;
    this.touch = copyMe.touch;
    this.self = copyMe.self;
    this.twinnable_1dd_ua7 = copyMe.twinnable_1dd_ua7;
    this.disease = copyMe.disease;
    this.contaminated = copyMe.contaminated;
    this.charm = copyMe.charm;
    this.sight = copyMe.sight;
    this.verbal = copyMe.verbal;
    this.somatic = copyMe.somatic;
    this.material = copyMe.material;
  }

  clone(drop_ids = false): AbilityLimitation {
    const new_obj = new AbilityLimitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  isEmpty(): boolean {
    return this.attack_types.length === 0 && 
      this.source_types.length === 0 && 
      this.source_ids.length === 0 && 
      this.name.length === 0 && 
      this.ability_tag.length === 0 &&
      this.damage_types.length === 0 &&
      this.attack_mods.length === 0;
  }
}
/**
 * These are limitations about spells. 
 */
export class SpellLimitation extends Limitation {
  spell_ids: string[] = [];
  excluded_spell_ids: string[] = [];
  source_type: DataType | "Any" = "Any";
  source_ids: string[] = [];
  list_ids: string[] = [];
  spell_list_selectable_key = "";
  schools: string[] = [];
  school_selectable_key = "";
  levels: SpellLevel[] = [];
  min_level: UpgradableNumber = new UpgradableNumber();
  max_level: UpgradableNumber = new UpgradableNumber();
  target_types: TargetType[] = [];
  area_types: AreaType[] = [];
  attack_types: AttackType[] = [];
  effect_types: EffectType[] = [];
  damage_types: (DamageType | "Selectable")[] = [];
  damage_selectable_key = "";
  casting_times: CastingTime[] = [];
  special_casting_time = "";
  ritual: "Yes" | "No" | "Ignore" = "Ignore";
  concentration: "Yes" | "No" | "Ignore" = "Ignore";
  min_duration: number | null = null;
  max_duration: number | null = null;
  min_range: number | null = null;
  max_range: number | null = null;
  touch: "Yes" | "No" | "Ignore" = "Ignore"; 
  self: "Yes" | "No" | "Ignore" = "Ignore";
  twinnable_1dd_ua7: "Yes" | "No" | "Ignore" = "Ignore";
  disease: "Yes" | "No" | "Ignore" = "Ignore";
  contaminated: "Yes" | "No" | "Ignore" = "Ignore";
  charm: "Yes" | "No" | "Ignore" = "Ignore";
  sight: "Yes" | "No" | "Ignore" = "Ignore";
  verbal: "Yes" | "No" | "Ignore" = "Ignore";
  somatic: "Yes" | "No" | "Ignore" = "Ignore";
  material: "Yes" | "No" | "Ignore" = "Ignore";
  m_cost_listed: "Yes" | "No" | "Ignore" = "Ignore";
  m_consumed: "Yes" | "No" | "Ignore" = "Ignore";
  in_spellbook: "Yes" | "No" | "Ignore" = "Ignore";
  use_selectable = false;
  selectable_key = "";

  constructor(obj?: any) {
    super(obj);
    this.limitation_type = "Spell";
    if (obj) {
      this.spell_ids = obj.spell_ids ? obj.spell_ids : [];
      this.excluded_spell_ids = obj.excluded_spell_ids ? obj.excluded_spell_ids : [];
      this.source_type = obj.source_type ? obj.source_type : "Any";
      this.source_ids = obj.source_ids ? obj.source_ids : [];
      this.list_ids = obj.list_ids ? obj.list_ids : [];
      if (this.list_ids.includes("Any")) {
        this.list_ids = [];
      }
      this.spell_list_selectable_key = obj.spell_list_selectable_key ? obj.spell_list_selectable_key : "";
      this.levels = obj.levels ? obj.levels : [];
      this.min_level = obj.min_level ? new UpgradableNumber(obj.min_level) : new UpgradableNumber();
      this.max_level = obj.max_level ? new UpgradableNumber(obj.max_level) : new UpgradableNumber();
      this.schools = obj.schools ? obj.schools : [];
      if (this.schools.includes("Any")) {
        this.schools = [];
      }
      this.school_selectable_key = obj.school_selectable_key ? obj.school_selectable_key : "";
      this.target_types = obj.target_types ? obj.target_types : [];
      this.area_types = obj.area_types ? obj.area_types : [];
      this.attack_types = obj.attack_types ? obj.attack_types : [];
      this.effect_types = obj.effect_types ? obj.effect_types : [];
      this.damage_types = obj.damage_types ? obj.damage_types : [];
      this.damage_selectable_key = obj.damage_selectable_key ? obj.damage_selectable_key : "";
      this.casting_times = obj.casting_times ? obj.casting_times : [];
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
      this.ritual = obj.ritual ? obj.ritual : "Ignore";
      this.concentration = obj.concentration ? obj.concentration : "Ignore";
      this.min_duration = obj.min_duration ? obj.min_duration : null;
      this.max_duration = obj.max_duration ? obj.max_duration : null;
      this.min_range = obj.min_range ? obj.min_range : null;
      this.max_range = obj.max_range ? obj.max_range : null;
      this.touch = obj.touch ? obj.touch : "Ignore";
      this.self = obj.self ? obj.self : "Ignore";
      this.twinnable_1dd_ua7 = obj.twinnable_1dd_ua7 ? obj.twinnable_1dd_ua7 : "Ignore";
      this.disease = obj.disease ? obj.disease : "Ignore";
      this.contaminated = obj.contaminated ? obj.contaminated : "Ignore";
      this.charm = obj.charm ? obj.charm : "Ignore";
      this.sight = obj.sight ? obj.sight : "Ignore";
      this.verbal = obj.verbal ? obj.verbal : "Ignore";
      this.somatic = obj.somatic ? obj.somatic : "Ignore";
      this.material = obj.material ? obj.material : "Ignore";
      this.m_cost_listed = obj.m_cost_listed ? obj.m_cost_listed : "Ignore";
      this.in_spellbook = obj.in_spellbook ? obj.in_spellbook : "Ignore";
      this.use_selectable = obj.use_selectable ? obj.use_selectable : false;
      this.selectable_key = obj.selectable_key ? obj.selectable_key : "";
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      spell_ids: [...this.spell_ids],
      excluded_spell_ids: this.excluded_spell_ids,
      source_type: this.source_type,
      source_ids: [...this.source_ids],
      list_ids: this.list_ids,
      spell_list_selectable_key: this.spell_list_selectable_key,
      levels: [...this.levels],
      min_level: this.min_level.toDBObj(),
      max_level: this.max_level.toDBObj(),
      schools: [...this.schools],
      school_selectable_key: this.school_selectable_key,
      target_types: this.target_types,
      area_types: this.area_types,
      attack_types: [...this.attack_types],
      effect_types: [...this.effect_types],
      damage_types: [...this.damage_types],
      damage_selectable_key: this.damage_selectable_key,
      casting_times: [...this.casting_times],
      special_casting_time: this.special_casting_time,
      ritual: this.ritual,
      concentration: this.concentration,
      min_duration: this.min_duration,
      max_duration: this.max_duration,
      min_range: this.min_range,
      max_range: this.max_range,
      touch: this.touch,
      self: this.self,
      twinnable_1dd_ua7: this.twinnable_1dd_ua7,
      disease: this.disease,
      contaminated: this.contaminated,
      charm: this.charm,
      sight: this.sight,
      verbal: this.verbal,
      somatic: this.somatic,
      material: this.material,
      m_cost_listed: this.m_cost_listed,
      in_spellbook: this.in_spellbook,
      use_selectable: this.use_selectable,
      selectable_key: this.selectable_key
    };
  }

  copy(copyMe: SpellLimitation, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.spell_ids = [...copyMe.spell_ids];
    this.excluded_spell_ids = [...copyMe.excluded_spell_ids];
    this.source_type = copyMe.source_type;
    this.source_ids = [...copyMe.source_ids];
    this.list_ids = [...copyMe.list_ids];
    this.spell_list_selectable_key = copyMe.spell_list_selectable_key;
    this.levels = [...copyMe.levels];
    this.min_level = copyMe.min_level.clone();
    this.max_level = copyMe.max_level.clone();
    this.schools = [...copyMe.schools];
    this.school_selectable_key = copyMe.school_selectable_key;
    this.target_types = [...copyMe.target_types];
    this.area_types = [...copyMe.area_types];
    this.attack_types = [...copyMe.attack_types];
    this.effect_types = [...copyMe.effect_types];
    this.damage_types = [...copyMe.damage_types];
    this.damage_selectable_key = copyMe.damage_selectable_key;
    this.casting_times = [...copyMe.casting_times];
    this.special_casting_time = copyMe.special_casting_time;
    this.ritual = copyMe.ritual;
    this.concentration = copyMe.concentration;
    this.min_duration = copyMe.min_duration;
    this.max_duration = copyMe.max_duration;
    this.min_range = copyMe.min_range;
    this.max_range = copyMe.max_range;
    this.touch = copyMe.touch;
    this.self = copyMe.self;
    this.twinnable_1dd_ua7 = copyMe.twinnable_1dd_ua7;
    this.disease = copyMe.disease;
    this.contaminated = copyMe.contaminated;
    this.charm = copyMe.charm;
    this.sight = copyMe.sight;
    this.verbal = copyMe.verbal;
    this.somatic = copyMe.somatic;
    this.material = copyMe.material;
    this.m_cost_listed = copyMe.m_cost_listed;
    this.in_spellbook = copyMe.in_spellbook;
    this.use_selectable = copyMe.use_selectable;
    this.selectable_key = copyMe.selectable_key;
  }

  clone(drop_ids = false): SpellLimitation {
    const new_obj = new SpellLimitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  isEmpty(except: string[] = []): boolean {
    return (this.source_type === "Any" || except.includes("source_type")) && 
      (this.source_ids.length === 0 || except.includes("source_ids")) && 
      (this.list_ids.length === 0 || except.includes("list_ids")) &&
      (this.spell_list_selectable_key === "" || except.includes("spell_list_selectable_key")) &&
      (this.levels.length === 0 || except.includes("levels")) &&
      (this.min_level.isBlank() || except.includes("min_level")) &&
      (this.max_level.isBlank() || except.includes("max_level")) &&
      (this.schools.length === 0 || except.includes("schools")) && 
      (this.school_selectable_key === "" || except.includes("school_selectable_key")) &&
      (this.spell_ids.length === 0 || except.includes("spell_ids")) && 
      (this.excluded_spell_ids.length === 0 || except.includes("excluded_spell_ids")) &&
      (this.target_types.length === 0 || except.includes("target_types")) &&
      (this.area_types.length === 0 || except.includes("area_types")) &&
      (this.attack_types.length === 0 || except.includes("attack_types")) &&
      (this.effect_types.length === 0 || except.includes("effect_types")) &&
      (this.damage_types.length === 0 || except.includes("damage_types")) &&
      (this.casting_times.length === 0 || except.includes("casting_times")) &&
      (this.special_casting_time.length === 0 || except.includes("special_casting_time")) &&
      (this.ritual === "Ignore" || except.includes("ritual")) &&
      (this.concentration === "Ignore" || except.includes("concentration")) &&
      (this.min_duration === null || except.includes("min_duration")) &&
      (this.max_duration === null || except.includes("max_duration")) &&
      (this.min_range === null || except.includes("min_range")) &&
      (this.max_range === null || except.includes("max_range")) &&
      (this.touch === "Ignore" || except.includes("touch")) &&
      (this.self === "Ignore" || except.includes("self")) &&
      (this.twinnable_1dd_ua7 === "Ignore" || except.includes("twinnable_1dd_ua7")) &&
      (this.disease === "Ignore" || except.includes("disease")) &&
      (this.contaminated === "Ignore" || except.includes("contaminated")) &&
      (this.charm === "Ignore" || except.includes("charm")) &&
      (this.sight === "Ignore" || except.includes("sight")) &&
      (this.verbal === "Ignore" || except.includes("verbal")) &&
      (this.somatic === "Ignore" || except.includes("somatic")) &&
      (this.material === "Ignore" || except.includes("material")) &&
      (this.m_cost_listed === "Ignore" || except.includes("m_cost_listed")) &&
      (this.in_spellbook === "Ignore" || except.includes("in_spellbook")) &&
      (this.use_selectable === false || except.includes("use_selectable")) &&
      (this.selectable_key === "" || except.includes("selectable_key"));
  }
}
/**
 * These are limitations about creatures. 
 */
export class CreatureLimitation extends Limitation {
  creature_types: string[] = []; // Beast, Dragon, Humanoid, etc.
  subtypes: string[] = [];
  swarm?: boolean;
  age_categories: string[] = [];
  size_nums: number[] = [];
  min_challenge_rating = -1;
  max_challenge_rating = -1;
  allow_flight = true;
  allow_swimming = true;
  creature_ids: string[] = [];
  
  constructor(obj?: any) {
    super(obj);
    this.limitation_type = "Creature";
    if (obj) {
      this.creature_types = obj.creature_types ? obj.creature_types : [];
      this.subtypes = obj.subtypes ? obj.subtypes : [];
      this.age_categories = obj.age_categories ? obj.age_categories : [];
      this.size_nums = obj.size_nums ? obj.size_nums : [];
      this.min_challenge_rating = obj.min_challenge_rating !== undefined ? obj.min_challenge_rating : -1;
      this.max_challenge_rating = obj.max_challenge_rating !== undefined ? obj.max_challenge_rating : -1;
      this.allow_flight = obj.allow_flight !== undefined ? obj.allow_flight : true;
      this.allow_swimming = obj.allow_swimming !== undefined ? obj.allow_swimming : true;
      this.creature_ids = obj.creature_ids ? obj.creature_ids : [];
    }
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      creature_types: [...this.creature_types],
      subtypes: this.subtypes,
      age_categories: this.age_categories,
      size_nums: [...this.size_nums],
      min_challenge_rating: this.min_challenge_rating,
      max_challenge_rating: this.max_challenge_rating,
      allow_flight: this.allow_flight,
      allow_swimming: this.allow_swimming,
      creature_ids: [...this.creature_ids]
    };
  }

  toString() {
    let str = "";
    if (this.isEmpty()) {
      str = "Any Creature";
    } else {
      const strs: string[] = [];
      if (this.creature_types.length > 0) {
        if (this.creature_types.length === 1) {
          strs.push(`Creature Type: ${this.creature_types[0]}`);
        } else {
          strs.push(`Creature Type Options: ${this.creature_types.join(", ")}`);
        }
      }
      if (this.subtypes.length > 0) {
        if (this.subtypes.length === 1) {
          strs.push(`Creature Subtype: ${this.subtypes[0]}`);
        } else {
          strs.push(`Creature Subtype Options: ${this.subtypes.join(", ")}`);
        }
      }
      if (this.age_categories.length > 0) {
        if (this.age_categories.length === 1) {
          strs.push(`Creature Age Category: ${this.age_categories[0]}`);
        } else {
          strs.push(`Creature Age Category Options: ${this.age_categories.join(", ")}`);
        }
      }
      if (!this.allow_flight) {
        strs.push(`No Flying`);
      }
      if (!this.allow_swimming) {
        strs.push(`No Swimming`);
      }
      if (this.creature_ids.length > 0) {
        strs.push(`Specific Creatures: {creature=[${this.creature_ids}]}`);
      }
      if (this.min_challenge_rating > -1) {
        strs.push(`CR >= ${this.min_challenge_rating}`);
      }
      if (this.max_challenge_rating > -1) {
        strs.push(`CR <= ${this.max_challenge_rating}`);
      }
      if (this.size_nums.length > 0) {
        if (this.size_nums.length === 1) {
          strs.push(`Size: ${SIZE_MAP[this.size_nums[0]]}`);
        } else {
          strs.push(`Size Options: ${this.size_nums.map(o => SIZE_MAP[o]).join(", ")}`);
        }
      }
      str = strs.join("; ");
    }
    return str;
  }

  copy(copyMe: CreatureLimitation, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.creature_types = [...copyMe.creature_types];
    this.subtypes = [...copyMe.subtypes];
    this.age_categories = [...copyMe.age_categories];
    this.size_nums = [...copyMe.size_nums];
    this.min_challenge_rating = copyMe.min_challenge_rating;
    this.max_challenge_rating = copyMe.max_challenge_rating;
    this.allow_flight = copyMe.allow_flight;
    this.allow_swimming = copyMe.allow_swimming;
    this.creature_ids = [...copyMe.creature_ids];
  }

  clone(drop_ids = false): CreatureLimitation {
    const new_obj = new CreatureLimitation();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  isEmpty(): boolean {
    return this.creature_types.length === 0 && 
      this.subtypes.length === 0 &&
      this.age_categories.length === 0 &&
      this.size_nums.length === 0 && 
      this.min_challenge_rating === -1 && 
      this.max_challenge_rating === -1 && 
      this.allow_flight === true && 
      this.allow_swimming === true &&
      this.creature_ids.length === 0;
  }
}

export function LimitationConstructor(obj: any) {
  switch (obj.limitation_type) {
    case "Character": return new CharacterLimitation(obj);
    case "Item": return new ItemLimitation(obj);
    case "Weapon": return new WeaponLimitation(obj);
    case "Ability": return new AbilityLimitation(obj);
    case "Spell": return new SpellLimitation(obj);
    case "Creature": return new CreatureLimitation(obj);
  }
  return new Limitation(obj);
}
